import { DateFormat } from "@/constants/DateFormat";
import { GroupClassRegistrationModalTestID } from "@/constants/TestID";
import type { RecommendedGroupClassInstance } from "@/store/api/schedule/groupClasses";
import { Text } from "@/ui/core";
import SelectableCard from "@/ui/core/SelectableCard";
import { VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { formatISODate, getTimeRange } from "@/utils/date.util";
import { joinTestIDs } from "@/utils/string.util";

interface TimeSlotSelectorProps extends BaseViewProps {
  instances: RecommendedGroupClassInstance[];
  onSelect: (groupClassExternalId: string) => void;
  selectedInstanceId?: string;
}

const formatTimeSlotLabel = ({
  endTime,
  startTime,
}: Readonly<{ endTime: string; startTime: string }>): string => {
  const day = formatISODate({
    dateString: startTime,
    format: DateFormat.weekDay,
  });
  const timeRange = getTimeRange({
    endDate: endTime,
    startDate: startTime,
  });

  return `${day}s ${timeRange}`;
};

const TimeSlotSelector = ({
  instances,
  onSelect,
  selectedInstanceId,
  testID,
}: TimeSlotSelectorProps) => {
  const baseTestID = joinTestIDs(testID, "time-slot-selector");

  return (
    <VStack gap={18} testID={baseTestID} width="100%">
      <Text
        color={ColorToken.NeutralPrimary}
        testID={joinTestIDs(
          testID,
          GroupClassRegistrationModalTestID.timeSlotTitle,
        )}
        variant="body"
      >
        Please choose the time that works best for you:
      </Text>
      {instances.map((instance) => {
        const label = formatTimeSlotLabel({
          endTime: instance.endTime,
          startTime: instance.startTime,
        });

        return (
          <SelectableCard
            accessibilityLabel={label}
            isSelected={instance.groupClassExternalId === selectedInstanceId}
            key={instance.groupClassExternalId}
            label={label}
            onSelect={() => onSelect(instance.groupClassExternalId)}
            testID={joinTestIDs(baseTestID, instance.groupClassExternalId)}
          />
        );
      })}
    </VStack>
  );
};

export type { TimeSlotSelectorProps };
export { formatTimeSlotLabel };
export default TimeSlotSelector;
